import React, { useMemo } from "react";
import { Table } from "react-bootstrap";
import {
  Column,
  useFilters,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { useHistory } from "react-router-dom";
import { OrganisationState } from "../../store/createStore";
import {
  dateSort,
  DefaultColumnFilter,
  filterExpiry,
  filterRemainingBalance,
  SelectExpiredColumnFilter,
  SelectRemainingBalanceFilter,
} from "../Shared/TableFilters";
import { FilterIcon } from "../Shared/FilterIcon";
import { Icon } from "@fluentui/react";
import { alertCellStyling, alertRowStyling } from "../Shared/styling";
import { PaginationInput } from "../Shared/PaginationInput";
import { isValidDate } from "../Shared/utils";

export const OrganisationsTable = ({ organisations }: any) => {
  const history = useHistory();

  const data: Array<any> = useMemo(() => {
    return organisations.map((org: OrganisationState) => {
      return {
        organisationId: org.organisationId,
        name: org.name,
        periodStart: isValidDate(new Date(org.periodStart))
          ? new Date(org.periodStart).toLocaleDateString()
          : "-",
        periodEnd: isValidDate(new Date(org.periodStart))
          ? new Date(org.periodEnd).toLocaleDateString()
          : "-",
        recentUsageIn: Number(org.totalMessagesIn),
        recentUsageOut: Number(org.totalMessagesOut),
        broughtForward: Number(org.broughtForward),
        remainingBalance: Number(org.balance),
        bundleExpiry: "N/A",
        expiredMessages: Number(org.expired),
        recentUsageAverage: Math.round(org.recentUsageAverage),
        addedMessages: org.addedMessages,
      };
    });
  }, [organisations]);

  const columns: Array<Column> = useMemo(
    () => [
      {
        Header: "Organisation",
        accessor: "name", // accessor is the "key" in the data
        canFilter: false,
        disableFilters: true,
      },
      {
        Header: "Period Start",
        accessor: "periodStart",
        canFilter: false,
        disableFilters: true,
        sortType: dateSort,
      },
      {
        Header: "Period End",
        accessor: "periodEnd",
        canFilter: false,
        disableFilters: true,
        sortType: dateSort,
      },
      {
        Header: "Brought Forward",
        accessor: "broughtForward",
        disableFilters: true,
        // Filter: SliderColumnFilter,
        // filter: filterGreaterThan,
      },
      {
        Header: "Added",
        accessor: "addedMessages",
        canFilter: false,
        disableFilters: true,
      },
      {
        Header: "Usage In",
        accessor: "recentUsageIn",
        disableFilters: true,
        // Filter: SliderColumnFilter,
        // filter: filterGreaterThan,
      },
      {
        Header: "Usage Out",
        accessor: "recentUsageOut",
        disableFilters: true,
        // Filter: SliderColumnFilter,
        // filter: filterGreaterThan,
      },
      {
        Header: "Recent Usage Average",
        accessor: "recentUsageAverage",
        disableFilters: true,
        // Filter: SliderColumnFilter,
        // filter: filterGreaterThan,
      },
      {
        Header: "Expired",
        accessor: "expiredMessages",
        canFilter: false,
        disableFilters: true,
      },
      {
        Header: "Remaining Balance",
        accessor: "remainingBalance",
        Filter: SelectRemainingBalanceFilter,
        filter: filterRemainingBalance,
      },
      {
        Header: "Bundle Expiry",
        accessor: "bundleExpiry",
        Filter: SelectExpiredColumnFilter,
        filter: filterExpiry,
        sortType: dateSort,
      },
    ],
    []
  );

  const defaultColumn: any = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const initialState = {
    hiddenColumns: ["bundleExpiry", "recentUsageAverage"],
  };

  const tableInstance: any = useTable(
    { columns, data, defaultColumn, initialState },
    useFilters,
    useSortBy,
    usePagination
  );
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    tableInstance;

  return (
    <div>
      <Table
        striped
        bordered
        variant="light"
        responsive
        className="table-hover"
        {...getTableProps()}
      >
        <thead>
          {
            // Loop over the header rows
            headerGroups.map((headerGroup: any, i: any) => {
              // Apply the header row props
              return (
                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                  {
                    // Loop over the headers in each row
                    headerGroup.headers.map((column: any) => (
                      // Apply the header cell props
                      <th
                        style={{
                          minWidth: "100px",
                        }}
                        key={column.id}
                      >
                        {/* Add a sort direction indicator */}
                        <span
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                        >
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon className="m-1" iconName="SortDown" />
                            ) : (
                              <Icon className="m-1" iconName="SortUp" />
                            )
                          ) : // <Icon className="m-1" iconName="Sort" />
                          null}
                          {
                            // Render the header
                            column.render("Header")
                          }
                        </span>
                        <span>
                          {/* {column.canFilter ? column.render("Filter") : null} */}
                          {column.canFilter ? (
                            <FilterIcon column={column} />
                          ) : null}
                        </span>
                      </th>
                    ))
                  }
                </tr>
              );
            })
          }
        </thead>
        <tbody {...getTableBodyProps()}>
          {
            // Loop over the table rows
            page.map((row: any) => {
              // Prepare the row for display
              prepareRow(row);
              return (
                // Apply the row props
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  className={alertRowStyling(
                    row.values.bundleExpiry,
                    row.values.remainingBalance
                  )}
                >
                  {
                    // Loop over the rows cells
                    row.cells.map((cell: any) => {
                      // Apply the cell props
                      return (
                        <td
                          {...cell.getCellProps()}
                          style={{
                            cursor: "pointer",
                            ...alertCellStyling(cell),
                          }}
                          onClick={() =>
                            history.push(
                              `/organisations/${data[row.index].organisationId}`
                            )
                          }
                          key={`${cell.row.id} - ${cell.column.id}`}
                        >
                          {
                            // Render the cell contents
                            cell.render("Cell")
                          }
                        </td>
                      );
                    })
                  }
                </tr>
              );
            })
          }
        </tbody>
      </Table>
      <PaginationInput tableInstance={tableInstance} />
    </div>
  );
};
